import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { DialogProps, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import Box from "@mui/material/Box";
import { LoadingButton } from "@mui/lab";
import CkEditorComponent from "../../../components/content/CkEditorComponent";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { ContactMessageInterface } from "../../../interfaces/ContactInterface";
import { requestApi } from "../../../helpers/RequestApi";
import { DELETE, PATCH } from "../../../utils/MethodUtils";
import { CONTACT_MESSAGE_URL } from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import getErrorApi from "../../../helpers/GetErrorApi";

interface State {
  contactMessage: ContactMessageInterface;
  asAdmin: boolean;
}

const FeedbackMessageComponent: React.FC<State> = React.memo(
  ({ contactMessage, asAdmin }) => {
    const [maxWidth] = React.useState<DialogProps["maxWidth"]>("lg");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const user = useAppSelector((state: RootState) => state.globalState.user);
    const isAdmin = useAppSelector(
      (state: RootState) => state.globalState.isAdmin
    );
    const { t, i18n } = useTranslation();
    const [isMe] = React.useState(
      !contactMessage.isAdmin &&
        contactMessage.userIdentifier === user?.userIdentifier
    );
    const [loadingSave, setLoadingSave] = React.useState(false);
    const [loadingDelete, setLoadingDelete] = React.useState(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const ckEditorRef: any = useRef();
    const [edit, setEdit] = React.useState(false);
    const [openDialogForm, setOpenDialogForm] = React.useState(false);

    const handleOpen = React.useCallback(() => {
      setOpenDialogForm(true);
    }, []);

    const handleClose = React.useCallback(
      (force = false) => {
        if (!force && loadingDelete) {
          return;
        }
        setOpenDialogForm(false);
      },
      [loadingDelete]
    );

    const editAction = React.useCallback(() => {
      setEdit((x) => !x);
    }, []);

    const deleteContactMessage = React.useCallback(async () => {
      setLoadingDelete(true);
      const response = await requestApi({
        method: DELETE,
        path: CONTACT_MESSAGE_URL + "/" + contactMessage.id,
        allowError: false,
        token: token,
      });
      if (response.statusCode === 204) {
        toastr.success(
          t("word.success"),
          t("sentence.notification.message_deleted")
        );
        handleClose(true);
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoadingDelete(false);
    }, [contactMessage.id, handleClose, t, token]);

    const save = React.useCallback(async () => {
      setLoadingSave(true);
      const response = await requestApi({
        method: PATCH,
        path: CONTACT_MESSAGE_URL + "/" + contactMessage.id,
        allowError: false,
        token: token,
        body: {
          content: ckEditorRef.current.getValue(),
        },
      });
      if (response.statusCode === 200) {
        toastr.success(
          t("word.success"),
          t("sentence.notification.message_updated")
        );
        setEdit(false);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoadingSave(false);
    }, [contactMessage.id, t, token]);

    return (
      <>
        <Dialog
          maxWidth={maxWidth}
          fullScreen={fullScreen}
          onClose={() => handleClose()}
          open={openDialogForm}
        >
          <DialogTitle>{t("word.deleteContactMessage.title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("word.deleteContactMessage.question")}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button onClick={() => handleClose()}>{t("word.no")}</Button>
            <LoadingButton
              loading={loadingDelete}
              variant="contained"
              onClick={deleteContactMessage}
            >
              {t("word.yes")}
            </LoadingButton>
          </DialogActions>
        </Dialog>
        <Card
          variant="outlined"
          sx={{
            marginBottom: 2,
            maxWidth: "90%",
            ...(((asAdmin && contactMessage.isAdmin) || (!asAdmin && isMe)) && {
              marginRight: 0,
              marginLeft: "auto",
              backgroundColor: "rgba(96, 92, 168, 0.1)",
            }),
          }}
        >
          <CardContent sx={{ padding: "4px !important" }}>
            <Typography
              sx={{
                color: theme.palette.primary.main,
                fontWeight: "bold",
              }}
            >
              {asAdmin
                ? contactMessage.isAdmin
                  ? "NOVE"
                  : contactMessage.userIdentifier
                : isMe
                ? t("word.you")
                : "NOVE"}
            </Typography>
            {edit ? (
              <CkEditorComponent
                ref={ckEditorRef}
                text={contactMessage.content}
              />
            ) : (
              <div
                className="ck-content"
                dangerouslySetInnerHTML={{ __html: contactMessage.content }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                {asAdmin && isAdmin && contactMessage.isAdmin && (
                  <>
                    {!edit && (
                      <LoadingButton
                        variant="contained"
                        onClick={handleOpen}
                        sx={{ marginRight: 2 }}
                        color="error"
                      >
                        {t("word.delete")}
                      </LoadingButton>
                    )}
                    <LoadingButton
                      variant="contained"
                      onClick={editAction}
                      sx={{ marginRight: 2 }}
                    >
                      {t(edit ? "word.cancel" : "word.edit.word")}
                    </LoadingButton>
                  </>
                )}
                {edit && (
                  <LoadingButton
                    loading={loadingSave}
                    variant="contained"
                    onClick={save}
                    sx={{ marginRight: 2 }}
                  >
                    {t("word.save")}
                  </LoadingButton>
                )}
              </Box>
              <Typography variant="caption">
                {new Date(contactMessage.created).toLocaleString(
                  i18n.language,
                  {
                    dateStyle: "long",
                    timeStyle: "medium",
                    timeZone: "America/Cuiaba",
                  }
                )}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </>
    );
  }
);

export default FeedbackMessageComponent;
